import { Avatar, Badge, Button, Checkbox, Input, PageLoading, SettingsPanel } from "../../../shared";
import {
  useGetUserQuery,
  useUpdateUserMutation,
} from "../../../redux/services/users/usersApi";
import { Link, useParams } from "react-router-dom";
import { roles as platformRoles } from "../../../utils/route.utils";
import { capitalizeFirstLetter } from "../../../utils";
import Radio from "../../../shared/Radio";
import { useEffect, useMemo, useState } from "react";
import ButtonDropdown from "../../../shared/ButtonDropdown";
import { sendEmail } from "../../../redux/reducers/users/userSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useCrumbs } from "../../../hooks/useCrumbs";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import {
  useAssignUserRolesMutation,
  useGetRolesQuery,
} from "../../../redux/services/roles/rolesApi";
import {
  getUserPlatformLabel,
  getUserStatusMeta,
} from "./user.utils";
import AccountOverview from "./AccountOverview";

/**
 * Settings → Users → Show
 *
 * Tenant Roles are the primary access control surface for tenant users.
 * Platform Access (legacy `authority`) is preserved as informational
 * metadata for backwards compatibility with existing integrations and the
 * super-admin tooling.
 */
export const UserShow = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const crumbs = useCrumbs();
  const { data: userData, isLoading, isFetching } = useGetUserQuery(params.id);
  const { hasPermission } = usePermissions();
  const canReadRoles = hasPermission(PERMISSIONS.SETTINGS_ROLES_READ);
  const canManageUsers = hasPermission(PERMISSIONS.SETTINGS_USERS_MANAGE);
  const canManageRoles = hasPermission(PERMISSIONS.SETTINGS_ROLES_MANAGE);
  const { data: tenantRoles = [], isLoading: rolesLoading } = useGetRolesQuery(
    undefined,
    { skip: !canReadRoles }
  );
  const [updateUser] = useUpdateUserMutation();
  const [assignUserRoles, { isLoading: assigningRoles }] =
    useAssignUserRolesMutation();

  const loading = isLoading || isFetching || rolesLoading;
  const user = userData || {};
  const authRole = user.accountHolder ? "owner" : user.authority;
  const [role, setRole] = useState("");
  const [selectedTenantRoleIds, setSelectedTenantRoleIds] = useState([]);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    fullName: "",
    phone: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    crumbs.updateCrumbName({
      name: user.fullName,
      path: `/settings/users/${user._id}`,
    });
    crumbs.setLoading(false);
  }, [crumbs.loading, user._id]);

  useEffect(() => {
    crumbs.setLoading(true);
    setRole(authRole);
  }, [authRole]);

  useEffect(() => {
    const roleIds = (user.tenantRoles || []).map((tenantRole) =>
      tenantRole.id || tenantRole._id || tenantRole
    );
    setSelectedTenantRoleIds(roleIds);
  }, [user._id, user.tenantRoles]);
  useEffect(() => {
    setProfileForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      fullName: user.fullName || "",
      phone: user.phone || "",
    });
  }, [user._id, user.firstName, user.lastName, user.fullName, user.phone]);

  const statusMeta = useMemo(() => getUserStatusMeta(user), [user.active]);
  const platformLabel = useMemo(() => getUserPlatformLabel(user), [user]);

  function updateProfileField(id, value) {
    setProfileForm((current) => ({ ...current, [id]: value }));
  }

  const profileDirty =
    profileForm.firstName !== (user.firstName || "") ||
    profileForm.lastName !== (user.lastName || "") ||
    profileForm.fullName !== (user.fullName || "") ||
    profileForm.phone !== (user.phone || "");

  async function handleSaveProfile() {
    if (!canManageUsers || !profileDirty) return;

    try {
      setSavingProfile(true);
      await updateUser({
        id: user._id,
        data: profileForm,
      }).unwrap();
      toast.success("User profile updated.");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to update user profile.");
    } finally {
      setSavingProfile(false);
    }
  }

  function profileFieldLayout() {
    const displayName = user.fullName || user.email || "Unnamed user";

    return (
      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="font-semibold text-gray-900">Profile details</div>
            <p className="text-sm text-gray-500">
              Update how this user appears across the workspace.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge color={statusMeta.color} label={statusMeta.label} />
              {user.accountHolder && (
                <Badge color="purple" label="Account holder" />
              )}
              <Badge color="gray" label={platformLabel} />
            </div>
          </div>
          {actionsButton}
        </div>

        <div className="flex flex-col-reverse gap-6 md:grid md:grid-cols-7 md:gap-4">
          <div className="space-y-3 md:col-span-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                id="firstName"
                label="First name"
                value={profileForm.firstName}
                disabled={!canManageUsers}
                onChange={(target) =>
                  updateProfileField("firstName", target.value)
                }
              />
              <Input
                id="lastName"
                label="Last name"
                value={profileForm.lastName}
                disabled={!canManageUsers}
                onChange={(target) =>
                  updateProfileField("lastName", target.value)
                }
              />
              <Input
                id="fullName"
                label="Display name"
                value={profileForm.fullName}
                disabled={!canManageUsers}
                onChange={(target) =>
                  updateProfileField("fullName", target.value)
                }
                wrapperClasses="md:col-span-2"
              />
              <Input
                id="phone"
                label="Phone"
                value={profileForm.phone}
                disabled={!canManageUsers}
                onChange={(target) => updateProfileField("phone", target.value)}
                wrapperClasses="md:col-span-2"
              />
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
            {canManageUsers && profileDirty && (
              <div className="flex justify-end">
                <Button loading={savingProfile} onClick={handleSaveProfile}>
                  Save profile
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center md:col-span-3 md:col-start-5">
            <span className="mb-2 text-sm font-medium text-gray-500">Avatar</span>
            <Avatar
              size="lg"
              src={user.avatar || ""}
              name={displayName}
            />
          </div>
        </div>
      </div>
    );
  }

  function roleLayout(item, i) {
    return (
      <div
        className={`flex items-center space-x-3 p-3 bg-white rounded-b-lg ${
          canManageUsers ? "cursor-pointer" : "cursor-default opacity-80"
        }`}
        onClick={() => canManageUsers && setRole(item.auth)}
      >
        <div>
          <Radio
            id="role"
            checked={item.auth === role}
            disabled={!canManageUsers}
            onChange={() => canManageUsers && setRole(item.auth)}
          />
        </div>
        <div>
          <div className="text-gray-900 font-medium">
            {capitalizeFirstLetter(item.auth)}
          </div>
          <div className="text-gray-600">{item.description}</div>
        </div>
      </div>
    );
  }

  function onCancel() {
    setRole(authRole);
  }

  function onCancelTenantRoles() {
    const roleIds = (user.tenantRoles || []).map((tenantRole) =>
      tenantRole.id || tenantRole._id || tenantRole
    );
    setSelectedTenantRoleIds(roleIds);
  }

  function handleUpdateUser() {
    handleSave({ authority: role, type: role });
  }

  function handleSave(formData) {
    const data = { id: user._id, data: formData };
    updateUser(data);
  }

  async function handleSaveTenantRoles() {
    // Defensive: only send role ids that we know exist for this tenant.
    // Filters out stale/disabled/cross-tenant ids that could otherwise cause
    // a 403 from `ensureRoleBelongsToTenant` on the API.
    const knownTenantRoleIds = new Set(
      (tenantRoles || []).map((role) => role.id || role._id).filter(Boolean)
    );
    const sanitizedRoleIds = Array.from(
      new Set(
        (selectedTenantRoleIds || [])
          .map((id) => (typeof id === "string" ? id.trim() : id))
          .filter(Boolean)
          .filter((id) => knownTenantRoleIds.has(id))
      )
    );

    try {
      await assignUserRoles({
        id: user._id,
        tenantRoles: sanitizedRoleIds,
      }).unwrap();
      toast.success("User roles updated.", {
        toastId: "settings-user-tenant-roles",
      });
    } catch (error) {
      toast.error(error?.data?.message || "Unable to update user roles.", {
        toastId: "settings-user-tenant-roles-error",
      });
    }
  }

  function toggleTenantRole(roleId) {
    setSelectedTenantRoleIds((current) =>
      current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId]
    );
  }

  function tenantRoleLayout(item) {
    if (!item.length) {
      return (
        <div className="p-4 bg-white rounded-b-lg text-sm text-gray-500">
          No tenant roles assigned. Assign at least one role so this user has
          permissions in the app.
        </div>
      );
    }

    return (
      <div className="space-y-3 p-4 bg-white rounded-b-lg">
        {item.map((tenantRole) => (
          <div
            key={tenantRole.id}
            className="flex items-start justify-between rounded-lg border border-gray-200 p-3"
          >
            {canReadRoles ? (
              <Checkbox
                id={`user-tenant-role-${tenantRole.id}`}
                label={
                  <span>
                    <span className="block font-medium text-gray-900">
                      {tenantRole.name}
                    </span>
                    {tenantRole.description && (
                      <span className="block text-sm text-gray-500">
                        {tenantRole.description}
                      </span>
                    )}
                  </span>
                }
                checked={selectedTenantRoleIds.includes(tenantRole.id)}
                disabled={!canManageRoles}
                onChange={() => toggleTenantRole(tenantRole.id)}
              />
            ) : (
              <div>
                <div className="font-medium text-gray-900">
                  {tenantRole.name}
                </div>
                {tenantRole.description && (
                  <div className="text-sm text-gray-500">
                    {tenantRole.description}
                  </div>
                )}
              </div>
            )}
            {tenantRole.isSystemRole && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                System
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  async function email(type) {
    const email = await dispatch(
      sendEmail({ userId: params.id, type, parent: user.parentCompany })
    ).unwrap();

    toast(email.message, {
      type: email.status,
      toastId: "emailSent",
    });
  }

  const tenantRoleIds = (user.tenantRoles || []).map((tenantRole) =>
    tenantRole.id || tenantRole._id || tenantRole
  );
  const assignedTenantRoles = (user.tenantRoles || [])
    .map((tenantRole) => {
      if (tenantRole.name) return tenantRole;
      return tenantRoles.find(
        (role) => role.id === (tenantRole.id || tenantRole._id || tenantRole)
      );
    })
    .filter(Boolean);
  const visibleTenantRoles = canReadRoles ? tenantRoles : assignedTenantRoles;
  const tenantRolesChanged =
    selectedTenantRoleIds.slice().sort().join(",") !==
    tenantRoleIds.slice().sort().join(",");

  const actions = [
    {
      id: "resend",
      title: "Resend Activate Email",
      onClick: () => email("activate"),
    },
    {
      id: "change-pass",
      title: "Send Change Password email",
      onClick: () => email("changePassword"),
    },
    {
      id: "deactivate",
      title: "Deactivate User",
      onClick: () => handleSave({ active: false }),
    },
  ].filter((i) => {
    if (!canManageUsers) {
      return false;
    }
    if (user.active && i.id === "resend") {
      return false;
    }
    return true;
  });

  const actionsButton = (
    actions.length > 0 && (
      <ButtonDropdown size="xs" data={actions}>
        Actions
      </ButtonDropdown>
    )
  );

  if (loading && !user._id) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/settings/users"
        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
      >
        Back to users
      </Link>

      {profileFieldLayout()}

      <AccountOverview user={user} loading={loading} />

      {/* Tenant roles are the primary access control surface. */}
      <SettingsPanel
        itemLayout={tenantRoleLayout}
        title="Tenant Roles"
        subtitle="Roles control what this user can see and do in the app. Assign one or more roles from your customer-managed roles."
        data={[visibleTenantRoles]}
      />
      {canReadRoles && tenantRolesChanged && !loading && canManageRoles && (
        <div>
          <Button
            type="alternative"
            className="mr-2"
            onClick={onCancelTenantRoles}
          >
            Cancel
          </Button>
          <Button loading={assigningRoles} onClick={handleSaveTenantRoles}>
            Save Roles
          </Button>
        </div>
      )}

      {/* Platform Access is legacy authority. Kept for backwards
          compatibility — tenant roles drive runtime permissions. */}
      <SettingsPanel
        itemLayout={roleLayout}
        title="Platform Access"
        subtitle="Legacy access tier. Account holders have full tenant access automatically; other levels are kept for backwards compatibility while we phase them out. Tenant Roles above control day-to-day permissions."
        data={platformRoles.filter((i) => !i.hide)}
      />
      {role !== authRole && !loading && canManageUsers && (
        <div>
          <Button type="alternative" className="mr-2" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser}>Save</Button>
        </div>
      )}
    </div>
  );
};

